import { Button, ButtonDropdown, Spinner } from '@cimplico/components';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import Editor from '../../components/rich-text-editor/Editor';
import { toastNotification } from '../../components/toast/Toast';
import WorksheetOtherScheduleSelect from '../../components/worksheet-other-schedule-select/WorksheetOtherScheduleSelect';
import { useCreateClientQuery } from '../../queries/use-create-client-query';
import { useUpdateClientQueries } from '../../queries/use-update-client-queries';
import { ClientQuery, ClientQueryOverview, ClientQueryStatusEnum, UpdateClientQueryFields } from '../../types/client-query';
import { Worksheet } from '../../types/worksheet';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../intercom-events';
type NewOrEditClientQueryProps = {
    workpaperId: string;
    defaultClientQuery?: ClientQuery | ClientQueryOverview;
    isEditing?: boolean;
    onClose: () => void;
    onCreate: (id: string) => void;
};

function NewOrEditClientQuery({
    workpaperId,
    defaultClientQuery,
    isEditing = false,
    onClose,
    onCreate,
}: NewOrEditClientQueryProps): JSX.Element {
    const { trackEvent } = useIntercom();
    const [worksheet, setWorksheet] = useState(defaultClientQuery?.worksheet);
    const [query, setQuery] = useState<{ rawQuery?: string; structuredQuery?: { [key: string]: unknown } | null; errorMessage?: string }>({
        rawQuery: defaultClientQuery?.rawQuery,
        structuredQuery: defaultClientQuery?.structuredQuery,
    });

    const createClientQueryQuery = useCreateClientQuery(workpaperId);
    const updateClientQueryQuery = useUpdateClientQueries(workpaperId);

    useEffect(() => {
        setQuery({
            rawQuery: defaultClientQuery?.rawQuery,
            structuredQuery: defaultClientQuery?.structuredQuery,
        });
        setWorksheet(defaultClientQuery?.worksheet);
    }, [defaultClientQuery]);

    function handleQueryChange(rawContent: string, structuredContent: string): void {
        setQuery({ rawQuery: rawContent, structuredQuery: JSON.parse(structuredContent), errorMessage: '' });
    }

    /**
     * Sets the current context. Will be a worksheet (account worksheet or other schedule).
     * @param worksheets
     */
    function handleContextChange(worksheets: Worksheet[]): void {
        const worksheet = worksheets[0];

        if (worksheet) {
            setWorksheet({
                id: worksheet.id,
                name: worksheet.name,
                hasAccount: Boolean(worksheet.account),
                type: worksheet.type,
            });
        } else {
            setWorksheet(undefined);
        }
    }

    function setActiveQuery(newQueryId: string): void {
        updateClientQueryQuery.mutate([{ id: newQueryId, fields: { status: ClientQueryStatusEnum.ACTIVE } }], {
            onSuccess: () => {
                toastNotification('success', 'Client query marked as active!');
            },
        });
    }

    function handleNewQuerySubmit({ setActive }: { setActive: boolean }): void {
        if (!query.rawQuery || !query.structuredQuery) {
            setQuery({ rawQuery: query.rawQuery, structuredQuery: query.structuredQuery, errorMessage: 'A title is required.' });
            toastNotification('error', 'Unable to create new client query!');
            return;
        }

        createClientQueryQuery.mutate(
            {
                clientQuery: [
                    {
                        rawQuery: query.rawQuery,
                        structuredQuery: query.structuredQuery,
                        worksheetId: worksheet?.id,
                    },
                ],
            },
            {
                onSuccess: (res) => {
                    if (res.results[0].data?.id) {
                        onCreate(res.results[0].data?.id);
                        trackEvent(intercomEvents.clientQuery.created.event);
                        if (setActive) {
                            setActiveQuery(res.results[0].data?.id);
                        }
                    }
                },
            }
        );
    }

    function handleEditQuerySubmit({ setActive }: { setActive: boolean }): void {
        if (!query.rawQuery || !query.structuredQuery) {
            setQuery((prev) => ({ ...prev, errorMessage: 'A title is required.' }));
            toastNotification('error', 'Unable to edit client query!');
            return;
        }

        if (!defaultClientQuery) {
            return;
        }

        const fields: UpdateClientQueryFields = {};

        if (query.rawQuery !== defaultClientQuery.rawQuery) {
            fields.rawQuery = query.rawQuery;
        }
        if (JSON.stringify(query.structuredQuery) !== JSON.stringify(defaultClientQuery.structuredQuery)) {
            fields.structuredQuery = query.structuredQuery;
        }
        if (setActive && defaultClientQuery.status !== ClientQueryStatusEnum.ACTIVE) {
            fields.status = ClientQueryStatusEnum.ACTIVE;
        }
        if (worksheet?.id !== defaultClientQuery.worksheet?.id) {
            fields.worksheetId = worksheet?.id || null;
        }

        // If none of the details changed, return
        if (Object.keys(fields).length === 0) {
            onCreate(defaultClientQuery.id);
            return;
        }

        updateClientQueryQuery.mutate([{ id: defaultClientQuery.id, fields }], {
            onError: (e) => {
                const result = e.response?.data.results[0];
                setQuery((prev) => ({ ...prev, errorMessage: result.error?.fields?.name }));
            },
            onSuccess: () => {
                onCreate(defaultClientQuery.id);
                toastNotification('success', 'Client query updated!');
            },
        });
    }

    // Check if it is creating/editing a client query with active/draft status
    function handleSave({ setActive }: { setActive: boolean }): void {
        if (isEditing) {
            handleEditQuerySubmit({ setActive });
            return;
        }

        handleNewQuerySubmit({ setActive });
    }

    return (
        <div
            className={`h-[725px] pl-6 pr-2 pt-2 relative ${
                createClientQueryQuery.isLoading || updateClientQueryQuery.isLoading ? 'pointer-events-none' : ''
            }`}
        >
            {(createClientQueryQuery.isLoading || updateClientQueryQuery.isLoading) && (
                <div className="pointer-events-none absolute top-0 left-0 w-full h-full z-50 bg-white/60">
                    <div className="absolute top-1/3 left-1/2">
                        <Spinner />
                    </div>
                </div>
            )}
            <div className="flex items-center justify-between">
                <p className="text-2xl font-medium text-gray-800">{defaultClientQuery ? 'Edit' : 'New'} Query</p>
                <Button type="hollow" size="sm" leftIcon={<XMarkIcon />} onClick={onClose} />
            </div>

            <div className="flex flex-col gap-4 mt-8">
                <WorksheetOtherScheduleSelect
                    isMulti={false}
                    title="Context"
                    isOptional
                    onChange={handleContextChange}
                    form="new-client-query"
                    placeholder="Select a worksheet or schedule for this query..."
                    selectedIds={worksheet ? [worksheet.id] : []}
                />
                <div className="h-[300px]">
                    <Editor
                        placeholder="Enter a query..."
                        defaultContent={defaultClientQuery ? JSON.stringify(defaultClientQuery.structuredQuery) : undefined}
                        onContentChange={handleQueryChange}
                        errorMessage={query.errorMessage}
                    />
                </div>
                <div className="ml-auto mr-0">
                    <Button
                        type="hollow"
                        label="Cancel"
                        size="xs"
                        onClick={onClose}
                        disabled={createClientQueryQuery.isLoading || updateClientQueryQuery.isLoading}
                    />

                    {defaultClientQuery?.status === ClientQueryStatusEnum.ACTIVE ? (
                        <Button
                            type="primary"
                            label="Save"
                            size="xs"
                            disabled={updateClientQueryQuery.isLoading}
                            onClick={(): void => handleSave({ setActive: true })}
                        />
                    ) : (
                        <ButtonDropdown
                            variant="primary"
                            label="Save as Draft"
                            onClick={(): void => handleSave({ setActive: false })}
                            disabled={createClientQueryQuery.isLoading || updateClientQueryQuery.isLoading}
                            size="xs"
                        >
                            <ButtonDropdown.SecondaryAction label="Save as Active" onClick={(): void => handleSave({ setActive: true })} />
                        </ButtonDropdown>
                    )}
                </div>
            </div>
        </div>
    );
}
export default NewOrEditClientQuery;
